import _ from 'underscore';
import React, { useContext, useCallback, useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { withDefaultStyles, useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { withPerfMarker } from '@bingads-webui/performance-marker-react-wrapper';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Label, Checkbox, Link } from '@fluentui/react';
import { getStepStyles, SelectableCardList } from '@bingads-webui-campaign-react/conversion-wizard-components';
import { StepContainer } from '../step-container/step-container';
import { GoalCreationWizardContext } from '../../goal-creation-wizard-context';
import { getErrorMessage, getPreferredTag, processPayload, saveGoal } from '../../conversion-goals-repository';
import { WIZARD_ACTIONS } from '../../goal-creation-wizard-reducer';
import { endActivity } from '../../shared/util';

const isNullorUndefined = value => _.isNull(value) || _.isUndefined(value);

const EnhancedConversionsOptions = ({ i18n }) => [
  {
    id: 'true',
    title: i18n.getString(_TL_('Turn on Enhanced conversions')),
    description: (
      <ReactTemplate
        model={{
              learnMoreLink: text => (<a href="https://help.ads.microsoft.com/#apex/ads/en/60178/-1" aria-label={text} target="_blank" rel="noopener noreferrer">{text}</a>),
            }}
        template={i18n.getString(_TL_('Improve the accuracy of your conversions by matching them with personal information (i.e., email addresses or phone numbers) from people who convert on your website. Microsoft employs privacy-safe practices to safeguard this information and maintain compliance with data protection regulations. {{learnMoreLink}}Learn more{{/learnMoreLink}}.'))}
      />
    ),
    iconName: 'Link',
    footer: i18n.getString(_TL_('Recommended')),
    ariaLabel: i18n.getString(_TL_('Turn on Enhanced conversions')),
    descriptionValue: i18n.getString(_TL_('Improve the accuracy of your conversions by matching them with personal information (i.e., email addresses or phone numbers) from people who convert on your website. Microsoft employs privacy-safe practices to safeguard this information and maintain compliance with data protection regulations.')),
  },
  {
    id: 'false',
    title: i18n.getString(_TL_("I don't want to use enhanced conversions")),
    description: null,
    ariaLabel: i18n.getString(_TL_("I don't want to use enhanced conversions")),
  },
];

const EnhancedConversionsUnstyled = (props) => {
  const {
    classes,
  } = props;
  const noTagGoalTypes = ['ApplicationInstallGoal', 'InStoreTransactionGoal', 'OfflineConversionGoal', 'InStoreVisitGoal'];
  const i18n = useContext(I18nContext);
  const wizardContext = useContext(GoalCreationWizardContext);
  const { logActivity } = wizardContext;
  const [isEnhancedConversionsEnabled, setIsEnhancedConversionsEnabled] = useState(wizardContext.goalContext.IsEnhancedConversionsEnabled);
  const [isTermAndConditionAccepted, setIsTermAndConditionAccepted] = useState(wizardContext.goalContext.IsEnhancedConversionsEnabled);
  const [showTermAndConditionWarning, setShowTermAndConditionWarning] = useState(false);

  const [notificationError, setNotificationError] = useState(null);
  const [footerEnabled, setFooterEnabled] = useState(true);
  const isSaveDisabled = useMemo(() => isNullorUndefined(isEnhancedConversionsEnabled) || (isEnhancedConversionsEnabled && !isTermAndConditionAccepted), [isEnhancedConversionsEnabled, isTermAndConditionAccepted]);

  const skipTagSelectionStep = wizardContext.goalContext.Type && noTagGoalTypes.indexOf(wizardContext.goalContext.Type) > -1;

  const [selectedTile, setSelectedTile] = useState(isNullorUndefined(isEnhancedConversionsEnabled) ? null : isEnhancedConversionsEnabled.toString());

  const cardStyles = useDefaultStyles(() => ({
    footer: {
      color: 'black',
      fontWeight: 600,
      fontSize: '12px',
      width: 'fit-content',
      background: '#DFF6DD',
      padding: '2px 8px',
    },
    selectableButtonLarge: {
      height: 187,
    },
  }));
  const onChangeEnConvSelection = useCallback((id) => {
    logActivity.trace(`Enhanced Conversions:Setting to ${id}`);
    if (id === 'false') {
      setIsTermAndConditionAccepted(false);
    }
    setIsEnhancedConversionsEnabled(id === 'true');
    setSelectedTile(id);
  }, [logActivity]);

  const goalDelta = {
    IsEnhancedConversionsEnabled: isEnhancedConversionsEnabled && isTermAndConditionAccepted,
  };

  const handleNext = useCallback(() => {
    if (isEnhancedConversionsEnabled && !isTermAndConditionAccepted) {
      setShowTermAndConditionWarning(true);
      return;
    }

    const goalObj = { ...wizardContext.goalContext, ...goalDelta };

    if (skipTagSelectionStep) {
      saveGoal(processPayload(goalObj, wizardContext.permissions), wizardContext)
        .then((response) => {
          endActivity(logActivity);
          wizardContext.dispatch({
            type: WIZARD_ACTIONS.NAVIGATE_TO_LAST_STEP,
            data: {
              ...goalObj,
              Id: response.Id,
            },
            switchToEdit: true,
          });
        })
        .catch((response) => {
          setFooterEnabled(true);
          setNotificationError(getErrorMessage(response, i18n));
        });
      setFooterEnabled(false);
    } else {
      wizardContext.tagPromise.then((tags) => {
        const tag = _.isEmpty(wizardContext.goalContext.Tag) ? getPreferredTag(tags) : wizardContext.goalContext.Tag;
        const updatedTag = tag ? { Tag: tag } : {};
        endActivity(logActivity);
        if (tag && tag.isAutoCreated) {
          const goalToSave = {
            ...goalObj,
            Tag: {
              Id: tag.Id,
            },
          };
          saveGoal(processPayload(goalToSave, wizardContext.permissions), wizardContext).then((response) => {
            wizardContext.dispatch({
              type: WIZARD_ACTIONS.NAVIGATE_WITH_TAGINSTALL_OPTIONS,
              data: {
                ...goalToSave,
                Id: response.Id,
                ...updatedTag,
                tags,
              },
              tagInstallOption: 'no',
            });
          })
            .catch((response) => {
              setFooterEnabled(true);
              setNotificationError(getErrorMessage(response, i18n));
            });
        } else {
          wizardContext.dispatch({
            type: WIZARD_ACTIONS.WIZARD_SAVE_AND_NAVIGATE_TAG_SELECTION,
            data: {
              ...goalObj,
              ...updatedTag,
              tags,
            },
          });
        }
      }).catch((tagResponse) => {
        logActivity.error(`Could not retrieve tags for goal creation:${JSON.stringify(tagResponse)}`);
        notificationApi.showWarning(i18n.getString(_TL_('Could not fetch UET tags for your account. Please try again later')));
      });
    }
  }, [isEnhancedConversionsEnabled, isTermAndConditionAccepted, skipTagSelectionStep, wizardContext, goalDelta, logActivity, i18n]);

  useEffect(() => {
    let notificationId;
    if (!_.isEmpty(notificationError)) {
      notificationId = notificationApi.showWarning(notificationError);
    }
    return () => notificationId && notificationApi.dismiss(notificationId);
  }, [notificationError]);

  const footerConfig = useMemo(() => ({
    buttons: [{
      type: 'Secondary',
      onClick: wizardContext.goBack,
      isEnabled: footerEnabled,
      text: i18n.getString(_TL_('Back')),
    },
    {
      type: 'Primary',
      onClick: handleNext,
      isEnabled: footerEnabled && !isSaveDisabled,
      text: i18n.getString(_TL_('Save and next')),
    },
    {
      type: 'Cancel',
      onClick: wizardContext.cancel,
      isEnabled: footerEnabled,
      text: i18n.getString(_TL_('Cancel')),
    }],
  }), [wizardContext.goBack, wizardContext.cancel, footerEnabled, i18n, handleNext, isSaveDisabled]);

  const renderTermAndConditionLabel = useCallback(() => (
    <div className="term-and-condition-label">
      <ReactTemplate
        model={{
              terms: text => (<Link href="https://about.ads.microsoft.com/en-us/resources/policies/microsoft-advertising-agreement" aria-label={text} target="_blank" rel="noopener noreferrer" underline>{text}</Link>),
              policy: text => (<Link href="https://privacy.microsoft.com/en-us/privacystatement" aria-label={text} target="_blank" rel="noopener noreferrer" underline>{text}</Link>),
            }}
        template={i18n.getString(_TL_('By enabling enhanced conversion tracking, you agree to Microsoft {{terms}}terms of use{{/terms}} and {{policy}}privacy policy{{/policy}}.'))}
      />
    </div>
  ), [i18n]);

  return (
    <StepContainer
      title={i18n.getString(_TL_('Set up Enhanced conversions'))}
      step="enhanced-conversions"
      footerConfig={footerConfig}
    >
      <div className={classes.contentBlock}>
        {/* main goal selection */}
        {/* add help popup */}
        <Label>
          {i18n.getString(_TL_('Note: Setting up enhanced conversions will only apply to this conversion goal.'))}
        </Label>
        <SelectableCardList
          classes={cardStyles}
          contents={EnhancedConversionsOptions({ i18n })}
          onItemSelected={onChangeEnConvSelection}
          selectedItem={selectedTile}
          accessibleTitle={i18n.getString(_TL_('Select enhanced conversion setting'))}
          ariaLabel={i18n.getString(_TL_('Select enhanced conversion setting'))}
        />
        {isEnhancedConversionsEnabled && (
        <div className={classNames('enhanced-conv-terms-and-conditions')}>
            {/* eslint-disable-next-line react/jsx-indent */}
            <h3 className={classes.nonAccordionTitle}>{i18n.getString(_TL_('Enable Enhanced conversions for this goal'))}</h3>
          <Checkbox
            checked={isTermAndConditionAccepted}
            onChange={(ev, checked) => setIsTermAndConditionAccepted(checked)}
            onRenderLabel={renderTermAndConditionLabel}
            ariaLabel={i18n.getString(_TL_('checkbox: By enabling enhanced conversion tracking, you agree to Microsoft terms of use and privacy policy.'))}
          />
          {showTermAndConditionWarning && (
          <div className={classNames('enhanced-conv-tc-warning')}>
            {i18n.getString(_TL_('Please agree to Microsoft terms of use and privacy policy.'))}
          </div>)}
        </div>
          )}
      </div>
    </StepContainer>
  );
};

EnhancedConversionsUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getStepStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
};

export const EnhancedConversions = withPerfMarker(withDefaultStyles(EnhancedConversionsUnstyled, getStepStyles));
